import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api } from '../api.js';
import { ActivityCalendar } from '../components/ActivityCalendar.js';
import { LogPanel } from '../components/LogPanel.js';
import { WeekRing } from '../components/WeekRing.js';
import { WeightTrendCard } from '../components/WeightTrendCard.js';
import { useActivityCalendar } from '../hooks/useActivityCalendar.js';
import { useDaysRange } from '../hooks/useDaysRange.js';
import { useEntitlement } from '../hooks/useEntitlement.js';
import { useProfile } from '../hooks/useProfile.js';
import { useProgress } from '../hooks/useProgress.js';

const RECAP_DISMISSED_KEY = 'trial_recap_dismissed_v1';

function daysUntil(isoDate: string): number {
  return Math.max(0, Math.ceil((new Date(isoDate).getTime() - Date.now()) / 86_400_000));
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function minusDays(isoDate: string, n: number): string {
  const d = new Date(`${isoDate}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}

/**
 * Trial-ending recap banner (ADR-0012 §6).
 * Shown once per trial when ≤2 days remain. ED-safe — no pressure copy.
 */
function TrialRecapBanner() {
  const { t } = useTranslation('paywall');
  const { data: entitlement } = useEntitlement();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(RECAP_DISMISSED_KEY) === '1';
    } catch {
      return false;
    }
  });

  const today = todayIso();
  const from14 = minusDays(today, 13);

  const inTrial = entitlement?.inTrial ?? false;
  const daysLeft = entitlement?.currentPeriodEnd ? daysUntil(entitlement.currentPeriodEnd) : null;
  const shouldFetch = inTrial && daysLeft !== null && daysLeft <= 2 && !dismissed;

  const { data: range } = useQuery({
    queryKey: ['days', 'range', from14, today],
    queryFn: async () => {
      const { data, error } = await api.GET('/me/days', {
        params: { query: { from: from14, to: today } },
      });
      if (error || !data) throw new Error('Failed to load days range');
      return data;
    },
    enabled: shouldFetch,
    staleTime: 5 * 60_000,
    retry: false,
  });

  function dismiss() {
    setDismissed(true);
    try {
      localStorage.setItem(RECAP_DISMISSED_KEY, '1');
    } catch {
      // storage unavailable — state-only dismiss is fine
    }
  }

  if (!inTrial || daysLeft === null || daysLeft > 2 || dismissed) return null;

  const days = range?.days ?? [];
  const daysLogged = days.filter((d) => d.entryCount > 0).length;
  const proteinDaysOnTrack = days.filter((d) => {
    const target = d.target?.proteinG;
    const consumed = d.consumed?.proteinG;
    return target && consumed && consumed >= target * 0.9;
  }).length;

  return (
    <div className="rounded-2xl border border-mist/20 bg-mist/10 px-5 py-5 dark:border-mist/20 dark:bg-mist/10">
      <div className="flex items-start justify-between gap-3 mb-2">
        <p className="text-base font-semibold text-ink dark:text-ink">{t('recap.title')}</p>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="text-ink-soft hover:text-ink-soft dark:hover:text-ink shrink-0"
        >
          ✕
        </button>
      </div>

      {daysLogged > 0 && (
        <div className="mb-3 flex gap-6">
          <div>
            <p className="text-2xl font-bold text-leaf dark:text-mist">{daysLogged}</p>
            <p className="text-xs text-mist dark:text-mist">{t('recap.daysLogged')}</p>
          </div>
          {proteinDaysOnTrack > 0 && (
            <div>
              <p className="text-2xl font-bold text-leaf dark:text-mist">{proteinDaysOnTrack}/7</p>
              <p className="text-xs text-mist dark:text-mist">{t('recap.proteinOnTrack')}</p>
            </div>
          )}
        </div>
      )}

      <p className="mb-4 text-sm leading-5 text-leaf dark:text-mist">{t('recap.body')}</p>

      <div className="flex items-center gap-4">
        <Link
          to="/settings#plan"
          className="rounded-xl bg-leaf px-5 py-2.5 text-sm font-semibold text-white
                     hover:bg-leaf-deep focus:outline-none focus:ring-2 focus:ring-leaf"
          onClick={dismiss}
        >
          {t('recap.cta')}
        </Link>
        <button
          type="button"
          onClick={dismiss}
          className="text-xs text-mist hover:text-leaf dark:text-mist"
        >
          {t('recap.dismiss')}
        </button>
      </div>
    </div>
  );
}

function WeekRingCard() {
  const { data: progress } = useProgress();

  // ADR-0020: ring renders from the first log — hide when no days filled yet.
  if (progress?.enabled !== true || !progress.days.some(Boolean)) return null;

  const filledCount = progress.days.filter(Boolean).length;

  return (
    <div className="rounded-2xl border border-husk bg-white p-5 dark:border-ink/20 dark:bg-shell">
      <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-ink-soft">This week</p>
      <WeekRing weekStartDate={progress.weekStartDate} days={progress.days} />
      <p className="mt-3 text-xs text-ink-soft">
        {filledCount} day{filledCount === 1 ? '' : 's'} logged this week
      </p>
    </div>
  );
}

type Period = '7d' | '28d';

function KpiSection() {
  const { t } = useTranslation('progress');
  const today = todayIso();
  const from28 = minusDays(today, 27);
  const [period, setPeriod] = useState<Period>('7d');
  const { data: range, isLoading } = useDaysRange(from28, today);

  const avg = period === '7d' ? range?.avg7 : range?.avg28;

  if (!isLoading && !avg) return null;

  return (
    <div className="rounded-2xl border border-husk bg-white p-5 dark:border-ink/20 dark:bg-shell">
      {/* Period toggle */}
      <div className="mb-4 flex gap-1 rounded-xl bg-husk p-1 dark:bg-ink/10">
        {(['7d', '28d'] as Period[]).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPeriod(p)}
            aria-pressed={period === p}
            className={`flex-1 rounded-lg py-1.5 text-xs font-medium transition-colors ${
              period === p
                ? 'bg-white text-ink shadow-sm dark:bg-shell dark:text-ink'
                : 'text-ink-soft hover:text-ink'
            }`}
          >
            {p === '7d' ? t('period7') : t('period28')}
          </button>
        ))}
      </div>

      {/* KPI cards */}
      {isLoading ? (
        <div className="grid grid-cols-4 gap-3">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-16 animate-pulse rounded-xl bg-husk dark:bg-ink/10" />
          ))}
        </div>
      ) : avg ? (
        <div className="grid grid-cols-4 gap-3">
          {(
            [
              { key: 'kpi.avgKcal', value: `${Math.round(avg.kcal)}` },
              { key: 'kpi.protein', value: `${Math.round(avg.proteinG)}g` },
              { key: 'kpi.carbs', value: `${Math.round(avg.carbsG)}g` },
              { key: 'kpi.fat', value: `${Math.round(avg.fatG)}g` },
            ] as const
          ).map(({ key, value }) => (
            <div key={key} className="rounded-xl border border-husk p-3 dark:border-ink/20">
              <p className="mb-1 text-xs uppercase tracking-wide text-ink-soft truncate">
                {t(key as Parameters<typeof t>[0])}
              </p>
              <p className="text-base font-bold text-ink">{value}</p>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function ActivityCalendarCard() {
  const today = todayIso();
  // Show last 12 months (up to 365 days).
  const from = minusDays(today, 364);
  const { data: cal } = useActivityCalendar(from, today);

  if (cal?.enabled !== true) return null;

  return (
    <div className="rounded-2xl border border-husk bg-white p-5 dark:border-ink/20 dark:bg-shell">
      <ActivityCalendar from={cal.from} to={cal.to} days={cal.days} totalLogged={cal.totalLogged} />
    </div>
  );
}

export default function HomePage() {
  const { t } = useTranslation('log');
  const { data: profile } = useProfile();
  return (
    <div className="flex flex-col gap-6">
      <h2 className="text-xl font-medium tracking-tight">{t('button')}</h2>
      {/* Log panel first — the primary daily action must be above the fold */}
      <LogPanel />
      {/* Progress widgets below — visible after the core action */}
      <WeekRingCard />
      <KpiSection />
      <ActivityCalendarCard />
      <WeightTrendCard profile={profile} compact showViewLink />
      <TrialRecapBanner />
    </div>
  );
}
